// Mirrors case-delivery-app/utilities/dashboardFunctions/contractFieldCatalogue.js.
export type ClientFieldType = 'name' | 'email' | 'phone' | 'dob' | 'postcode'
export type AddressFieldType = 'address_line_1' | 'city' | 'county' | 'full_address'
export type ClaimFieldType = 'case_ref' | 'lender' | 'policy_number' | 'start_date'
export type SystemFieldType = 'date_today'
export type TemplateFieldType =
    ClientFieldType | AddressFieldType | ClaimFieldType | SystemFieldType | 'custom_text' | 'signature'

/** Types whose value comes from the recipient record (typed, searched or from a sheet). */
export type DataFieldType = ClientFieldType | AddressFieldType | ClaimFieldType

/** @deprecated kept for older imports — use DataFieldType */
export type StandardFieldType = ClientFieldType

export type FieldGroup = 'client' | 'address' | 'claim' | 'system' | 'other'

export interface FieldDefinition {
    type: TemplateFieldType
    label: string
    group: FieldGroup
    /** Spreadsheet column that supplies the value; null when never sheet-supplied. */
    csvHeader: string | null
}

export const FIELD_DEFINITIONS: FieldDefinition[] = [
    { type: 'name', label: 'Name', group: 'client', csvHeader: 'Name' },
    { type: 'email', label: 'Email', group: 'client', csvHeader: 'Email' },
    { type: 'phone', label: 'Phone', group: 'client', csvHeader: 'Phone' },
    { type: 'dob', label: 'Date of Birth', group: 'client', csvHeader: 'Date of Birth' },
    { type: 'postcode', label: 'Postcode', group: 'client', csvHeader: 'Postcode' },
    { type: 'address_line_1', label: 'Address line 1', group: 'address', csvHeader: 'Address Line 1' },
    { type: 'city', label: 'Town / City', group: 'address', csvHeader: 'City' },
    { type: 'county', label: 'County', group: 'address', csvHeader: 'County' },
    { type: 'full_address', label: 'Full address', group: 'address', csvHeader: 'Full Address' },
    { type: 'case_ref', label: 'Case reference', group: 'claim', csvHeader: 'Case Ref' },
    { type: 'lender', label: 'Lender', group: 'claim', csvHeader: 'Lender' },
    { type: 'policy_number', label: 'Policy / agreement no.', group: 'claim', csvHeader: 'Policy Number' },
    { type: 'start_date', label: 'Agreement start date', group: 'claim', csvHeader: 'Start Date' },
    { type: 'date_today', label: "Today's date", group: 'system', csvHeader: null },
    { type: 'custom_text', label: 'Custom Text', group: 'other', csvHeader: null },
    { type: 'signature', label: 'Signature', group: 'other', csvHeader: null },
]

export const FIELD_GROUP_LABELS: Record<FieldGroup, string> = {
    client: 'Client',
    address: 'Address',
    claim: 'Claim',
    system: 'System',
    other: 'Other',
}

export const FIELD_GROUP_ORDER: FieldGroup[] = ['client', 'address', 'claim', 'system', 'other']

export const FIELD_TYPE_LABELS = Object.fromEntries(FIELD_DEFINITIONS.map((d) => [d.type, d.label])) as Record<
    TemplateFieldType,
    string
>

export const FIELD_BY_TYPE = Object.fromEntries(FIELD_DEFINITIONS.map((d) => [d.type, d])) as Record<
    TemplateFieldType,
    FieldDefinition
>

export const DATA_FIELD_TYPES = FIELD_DEFINITIONS.filter((d) => d.csvHeader).map((d) => d.type) as DataFieldType[]
export const CLIENT_FIELD_TYPES: ClientFieldType[] = ['name', 'email', 'phone', 'dob', 'postcode']
export const ADDRESS_FIELD_TYPES: AddressFieldType[] = ['address_line_1', 'city', 'county', 'full_address']
export const CLAIM_FIELD_TYPES: ClaimFieldType[] = ['case_ref', 'lender', 'policy_number', 'start_date']

/** @deprecated use CLIENT_FIELD_TYPES */
export const STANDARD_FIELD_ORDER = CLIENT_FIELD_TYPES

export const isDataFieldType = (type: string): type is DataFieldType => (DATA_FIELD_TYPES as string[]).includes(type)
export const isClaimFieldType = (type: string): type is ClaimFieldType => (CLAIM_FIELD_TYPES as string[]).includes(type)

export interface TemplateField {
    id: string
    type: TemplateFieldType
    label: string
    page: number // 1-based
    x: number // fraction of page width, origin top-left
    y: number // fraction of page height
    w: number
    h: number
}

export interface TemplateRow {
    id: number
    name: string
    fields: TemplateField[]
    created_by: string | null
    created_at: string | null
}

export type ContractStatus = 'Sent' | 'Viewed' | 'Signed' | 'Delivered'

export interface ContractRow {
    id: number
    token: string
    recipient_name: string | null
    recipient_email: string
    document_name: string | null
    status: ContractStatus | string
    sent_by: string | null
    created_at: string | null
    viewed_at: string | null
    signed_at: string | null
    delivery_type?: 'esign' | 'attachment' | string | null
    template_name?: string | null
    client_id?: number | null
    client_origin?: string | null
    case_ref?: string | null
    batch_id?: number | null
    batch_name?: string | null
    signing_link?: string | null
}

/** One document per client, or one per claim. */
export type Granularity = 'client' | 'claim'
/** Which claim supplies claim-field values for a client-level document. */
export type ClaimRule = 'latest' | 'oldest' | 'base'

export const CLAIM_RULE_LABELS: Record<ClaimRule, string> = {
    latest: 'Most recent claim',
    oldest: 'Oldest claim',
    base: 'Base claim',
}

export type BatchSource = 'sheet' | 'search' | 'filter' | 'identifiers' | 'single'
export type BatchStatus = 'draft' | 'running' | 'completed' | 'failed'

export const BATCH_SOURCE_LABELS: Record<BatchSource, string> = {
    sheet: 'Spreadsheet',
    search: 'Client search',
    filter: 'Introducer filter',
    identifiers: 'Identifier list',
    single: 'Send tab',
}

export interface BatchRow {
    id: number
    name: string | null
    template_id: number
    template_name?: string | null
    source: BatchSource | string
    granularity: Granularity | string
    claim_rule: ClaimRule | string | null
    status: BatchStatus | string
    delivery_type: 'esign' | 'attachment' | string
    total: number
    processed: number
    sent: number
    failed: number
    skipped?: number
    created_by: string | null
    created_at: string | null
    started_at: string | null
    finished_at: string | null
    last_error: string | null
    // Live signing counts (from contracts).
    viewed_count?: number
    signed_count?: number
    delivered_count?: number
}

export type BatchItemStatus = 'queued' | 'sent' | 'failed' | 'skipped'

export interface BatchItemRow {
    id: number
    batch_id: number
    row_index: number
    client_id: number | null
    client_origin: string | null
    case_ref: string | null
    recipient_name: string | null
    recipient_email: string | null
    merge_values?: Record<string, string> | string | null
    status: BatchItemStatus | string
    error: string | null
    contract_id: number | null
    // Joined from contracts.
    contract_status?: ContractStatus | string | null
    contract_token?: string | null
    delivery_type?: string | null
    viewed_at?: string | null
    signed_at?: string | null
    contract_created_at?: string | null
    signing_link?: string | null
}

/** A claim as returned by unified-client-search, trimmed to what contracts need. */
export interface ClaimSummary {
    case_ref: string
    lender: string
    policy_number: string
    start_date: string
    cancelled: boolean
    cfa_signed: boolean
}

// Client ids repeat across introducer databases, so (client_id, origin) is the identity.
export interface SelectedClient {
    client_id: number
    origin: string
    name: string
    first_name: string
    last_name: string
    email: string
    phone: string
    dob: string
    postcode: string
    address: { line1: string; city: string; county: string; full: string }
    claims: ClaimSummary[]
}

/** Values collected on the Send tab for the record-backed field types. */
export type RecipientValues = Record<DataFieldType, string>

export const EMPTY_RECIPIENT: RecipientValues = {
    name: '',
    email: '',
    phone: '',
    dob: '',
    postcode: '',
    address_line_1: '',
    city: '',
    county: '',
    full_address: '',
    case_ref: '',
    lender: '',
    policy_number: '',
    start_date: '',
}

export const recipientValuesFromClient = (client: SelectedClient, claim?: ClaimSummary | null): RecipientValues => ({
    name: client.name === 'Unnamed client' ? '' : client.name,
    email: client.email,
    phone: client.phone,
    dob: toDateInputValue(client.dob),
    postcode: client.postcode,
    address_line_1: client.address.line1,
    city: client.address.city,
    county: client.address.county,
    full_address: client.address.full,
    case_ref: claim?.case_ref || '',
    lender: claim?.lender || '',
    policy_number: claim?.policy_number || '',
    start_date: claim ? toDateInputValue(claim.start_date) : '',
})

export type ContractsTab = 'send' | 'bulk' | 'signing' | 'batches' | 'vault'

export const TAB_LABELS: Record<ContractsTab, string> = {
    send: 'Send',
    bulk: 'Bulk send',
    signing: 'Signing',
    batches: 'Bulk sends',
    vault: 'Contract Templates',
}

export const isContractsTab = (value: string | null): value is ContractsTab =>
    value === 'send' || value === 'bulk' || value === 'signing' || value === 'batches' || value === 'vault'

export const parseFields = (raw: unknown): TemplateField[] => {
    let fields = raw
    if (typeof fields === 'string') {
        try {
            fields = JSON.parse(fields || '[]')
        } catch {
            fields = []
        }
    }
    return Array.isArray(fields) ? (fields as TemplateField[]) : []
}

export const hasSignatureField = (fields: TemplateField[]) => fields.some((f) => f.type === 'signature')

export interface TemplateRequirements {
    hasSignature: boolean
    needsClaim: boolean
    /** Record-backed types the template stamps, in catalogue order. */
    dataTypes: DataFieldType[]
    customFields: { id: string; label: string; page: number }[]
}

export const templateRequirements = (fields: TemplateField[]): TemplateRequirements => {
    const present = new Set(fields.map((f) => f.type))
    return {
        hasSignature: present.has('signature'),
        needsClaim: CLAIM_FIELD_TYPES.some((t) => present.has(t)),
        dataTypes: DATA_FIELD_TYPES.filter((t) => present.has(t)),
        customFields: fields
            .filter((f) => f.type === 'custom_text')
            .map((f) => ({ id: f.id, label: f.label || 'Custom text', page: f.page })),
    }
}

/** Default granularity for a template: per claim when it stamps claim data. */
export const defaultGranularity = (fields: TemplateField[]): Granularity =>
    templateRequirements(fields).needsClaim ? 'claim' : 'client'

/**
 * Data types a client record cannot satisfy for a template. Email is always
 * required for delivery; everything else only when stamped. Claim types are
 * checked against the supplied claim (or reported missing when none given).
 */
export const missingForClient = (
    fields: TemplateField[],
    client: SelectedClient | null,
    claim?: ClaimSummary | null,
): DataFieldType[] => {
    if (!client) return []
    const values = recipientValuesFromClient(client, claim)
    const { dataTypes } = templateRequirements(fields)
    const missing: DataFieldType[] = []
    if (!values.email.trim()) missing.push('email')
    for (const type of dataTypes) {
        if (type === 'email') continue
        if (!values[type].trim()) missing.push(type)
    }
    return missing
}

/** Picks the claim a client-level document should merge, per the claim rule. */
export const pickClaimByRule = (claims: ClaimSummary[], rule: ClaimRule): ClaimSummary | null => {
    if (!claims.length) return null
    const active = claims.filter((c) => !c.cancelled)
    const pool = active.length ? active : claims
    if (rule === 'base') return pool[0]
    const time = (c: ClaimSummary) => {
        const t = new Date(c.start_date).getTime()
        return isNaN(t) ? 0 : t
    }
    const sorted = [...pool].sort((a, b) => time(a) - time(b))
    return rule === 'oldest' ? sorted[0] : sorted[sorted.length - 1]
}

export const formatDateTime = (value: string | null | undefined) => {
    if (!value) return '—'
    const parsed = new Date(value)
    if (isNaN(parsed.getTime())) return value
    return parsed.toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })
}

/** dd/mm/yyyy display for a date stored as ISO / yyyy-mm-dd / Date-parsable string. */
export const formatDob = (value: string) => {
    if (!value) return ''
    const parsed = new Date(value)
    if (isNaN(parsed.getTime())) return value
    return parsed.toLocaleDateString('en-GB')
}

export const formatDate = formatDob

/** yyyy-mm-dd for the date input from whatever the record holds. */
export const toDateInputValue = (value: string) => {
    if (!value) return ''
    const parsed = new Date(value)
    if (isNaN(parsed.getTime())) return ''
    const yyyy = parsed.getFullYear()
    const mm = String(parsed.getMonth() + 1).padStart(2, '0')
    const dd = String(parsed.getDate()).padStart(2, '0')
    return `${yyyy}-${mm}-${dd}`
}

export const statusTone = (status: string): 'good' | 'info' | 'warn' | 'muted' | 'bad' => {
    if (status === 'Signed' || status === 'Delivered') return 'good'
    if (status === 'Viewed') return 'warn'
    if (status === 'Sent') return 'info'
    if (status === 'failed') return 'bad'
    return 'muted'
}

export const batchStatusTone = (status: string): 'good' | 'info' | 'warn' | 'muted' | 'bad' => {
    if (status === 'completed') return 'good'
    if (status === 'running') return 'warn'
    if (status === 'failed') return 'bad'
    return 'muted'
}

export const itemStatusLabel = (item: BatchItemRow) => {
    if (item.status === 'failed') return 'Failed'
    if (item.status === 'skipped') return 'Skipped'
    if (item.status === 'queued') return 'Queued'
    return item.contract_status || 'Sent'
}

export const itemStatusTone = (item: BatchItemRow): 'good' | 'info' | 'warn' | 'muted' | 'bad' => {
    if (item.status === 'failed') return 'bad'
    if (item.status === 'skipped' || item.status === 'queued') return 'muted'
    return statusTone(item.contract_status || 'Sent')
}
